import { baseApi } from './baseApi';
import type { JsonObject, JsonValue } from './baseQuery';

interface ContractsResponse {
    contracts: Contract[];
}

export interface Contract extends JsonObject {
    resource_id: string;
    resource_type: string;
    product_id: string;
    level: string;
    state: string;
    product_name: string;
    product_level_name: string;
    product_variant_name: string;
    starts_at: string;
    ends_at: string;
    activation_state: string;
}

const isJsonObject = (value: JsonValue | null): value is JsonObject =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const isContract = (value: JsonValue): value is Contract =>
    isJsonObject(value) &&
    typeof value.resource_id === 'string' &&
    typeof value.resource_type === 'string' &&
    typeof value.product_id === 'string' &&
    typeof value.level === 'string' &&
    typeof value.state === 'string' &&
    typeof value.product_name === 'string' &&
    typeof value.product_level_name === 'string' &&
    typeof value.product_variant_name === 'string' &&
    typeof value.starts_at === 'string' &&
    typeof value.ends_at === 'string' &&
    typeof value.activation_state === 'string';

const toContract = (value: JsonValue): Contract => {
    if (!isContract(value)) {
        throw new Error('Invalid contracts response');
    }

    return value;
};

const mapContractsResponse = (value: JsonValue | null): ContractsResponse => {
    if (!isJsonObject(value) || !Array.isArray(value.contracts)) {
        return { contracts: [] };
    }

    return { contracts: value.contracts.filter(isContract).map(toContract) };
};

export const contractsApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getActiveContracts: build.query<Contract[], string>({
            query: assetId => ({
                service: 'marketplace',
                path: '/contracts',
                params: { resource_id: assetId, paid: true, exclude_periods: 'p1d' },
            }),
            transformResponse: rawResult => mapContractsResponse(rawResult).contracts,
            providesTags: (_result, _error, assetId) => [{ type: 'Contracts', id: assetId }],
        }),
        bulkGetActiveContracts: build.query<(Contract & { id: string })[], string[]>({
            async queryFn(assetIds, _queryApi, _extraOptions, baseQuery) {
                const contractsForAllAssets: (Contract & { id: string })[] = [];
                for (const assetId of assetIds) {
                    const result = await baseQuery({
                        service: 'marketplace',
                        path: '/contracts',
                        params: { resource_id: assetId, paid: true, exclude_periods: 'p1d' },
                    });
                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }
                    const contracts = mapContractsResponse(result.data).contracts;
                    contractsForAllAssets.push(...contracts.map(contract => ({ ...contract, id: assetId })));
                }
                return { data: contractsForAllAssets };
            },
            providesTags: (_result, _error, assetIds) => assetIds.map(id => ({ type: 'Contracts' as const, id })),
        }),
    }),
});

export const { useGetActiveContractsQuery, useBulkGetActiveContractsQuery } = contractsApi;
